// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Confirmation for the two consequential college operations (**ADR-0076** §4e).
 *
 * `docs/09-UX.md` requires a destructive action to state its consequences
 * before it is taken, and both of these have consequences an operator cannot
 * discover by trying them:
 *
 * - **deactivation cannot be undone from the appliance side.** A later Edge
 *   claim or readiness report does not reactivate a withdrawn college —
 *   deactivation always overrides readiness (**ADR-0076** §2) — so an operator
 *   who expects the appliance to "come back" is expecting something that cannot
 *   happen; and
 * - **deletion permanently reserves the identifier.** The tenant identifier and
 *   the college code go into an immutable tombstone and can never be registered
 *   again. That is deliberate — a reusable identifier is what let a deleted
 *   college's appliance be inherited by its successor — and it means a deletion
 *   made to "start over under the same code" destroys the very thing it was
 *   meant to free.
 *
 * The consequences are listed rather than summarised, because a single sentence
 * is what an operator skims. The confirming control names the operation instead
 * of saying "OK", so the last thing read before committing is what is about to
 * happen.
 *
 * **Typing the college code.** An irreversible operation may additionally
 * require the operator to type an exact value before the confirming control
 * becomes usable. Reading a consequence and acting on it are different acts,
 * and a dialog dismissed by reflex proves only that a button was reachable;
 * transcribing the code cannot be done without looking at which college is
 * named. The requirement is opt-in per caller, so the reversible operations
 * that share this component are not made tedious by a guard they do not need.
 */

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/** Props for {@link ConfirmActionDialog}. */
export interface ConfirmActionDialogProps {
  /** Dialog heading, naming the operation and the college. */
  readonly title: string;
  /** Leading sentence describing what is about to happen. */
  readonly summary: string;
  /** The consequences, one per list item. */
  readonly consequences: readonly string[];
  /** Label of the confirming control; names the operation, never "OK". */
  readonly confirmLabel: string;
  /** Label shown while the operation is in flight. */
  readonly pendingLabel: string;
  /** Whether the operation is in flight; both controls are inert while it is. */
  readonly submitting: boolean;
  /** Sanitised failure text from the last attempt, if any. */
  readonly failure: string | null;
  /**
   * Exact text the operator must type before confirming becomes possible.
   *
   * Omitted for operations that do not warrant the friction. When present, the
   * confirming control stays disabled until the field matches this value
   * exactly, so the guard cannot be satisfied by a near-miss.
   */
  readonly confirmationValue?: string;
  /** Field label naming what must be typed; required with `confirmationValue`. */
  readonly confirmationLabel?: string;
  /** Perform the operation. */
  readonly onConfirm: () => void;
  /** Close without performing it. */
  readonly onClose: () => void;
}

const TITLE_ID = 'confirm-action-title';
const DESCRIPTION_ID = 'confirm-action-description';

/** Confirm a consequential college operation. */
export function ConfirmActionDialog({
  title,
  summary,
  consequences,
  confirmLabel,
  pendingLabel,
  submitting,
  failure,
  confirmationValue,
  confirmationLabel,
  onConfirm,
  onClose,
}: ConfirmActionDialogProps): ReactNode {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmationRef = useRef<HTMLInputElement | null>(null);
  const [typed, setTyped] = useState('');

  const guarded = confirmationValue !== undefined;
  // Trimmed, because a trailing space from a paste is not a different college;
  // otherwise exact, so `DEMO` cannot stand in for `DEMO-COLLEGE`.
  const confirmed = !guarded || typed.trim() === confirmationValue;

  // Focus enters on the non-destructive control. A confirmation whose
  // destructive button is focused turns a stray `Enter` into the deletion the
  // dialog exists to slow down.
  //
  // A guarded dialog focuses its field instead: the destructive control is
  // disabled until the field matches, so a stray `Enter` cannot reach it, and
  // landing on the input is where the operator has to act anyway.
  useEffect(() => {
    if (guarded) {
      confirmationRef.current?.focus();
      return;
    }
    cancelRef.current?.focus();
  }, [guarded]);

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      aria-describedby={DESCRIPTION_ID}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle id={TITLE_ID}>{title}</DialogTitle>
      <DialogContent>
        {failure === null ? null : (
          <Alert severity="error" role="alert" sx={{ mb: 2 }} data-testid="confirm-action-error">
            {failure}
          </Alert>
        )}
        <Typography id={DESCRIPTION_ID} variant="body1" component="p">
          {summary}
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
          {consequences.map((consequence) => (
            <li key={consequence}>
              <Typography variant="body2">{consequence}</Typography>
            </li>
          ))}
        </Box>
        {guarded ? (
          <TextField
            inputRef={confirmationRef}
            label={confirmationLabel}
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
            disabled={submitting}
            fullWidth
            margin="normal"
            autoComplete="off"
            spellCheck={false}
            helperText={`Type ${confirmationValue} to enable ${confirmLabel}.`}
            slotProps={{ htmlInput: { 'data-testid': 'confirm-action-typed' } }}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} ref={cancelRef}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={submitting || !confirmed}
          data-testid="confirm-action-submit"
        >
          {submitting ? pendingLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
